import React, {
  ReactElement,
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Redirect } from "react-router-dom";

import { NavLoader } from "cortex/ui/src/ui/loader";
import { genCancelSourceOptions } from "cortex/ui/src/util/request";
import { Container } from "cortex/ui/src/view/layout";

interface FetchControllerProps<
  TObject = unknown,
  TObjectId extends string = string,
> {
  children?: ReactElement;
  fetchObject: (
    objectId: TObjectId,
    params: unknown,
    options: unknown,
  ) => Promise<unknown>;
  injectAs?: string;
  object?: TObject | null;
  objectId: TObjectId;
  objectLoaded: boolean;
  unloadObject?: () => void;
}

interface FetchState<TObjectId extends string = string> {
  inFlight: boolean;
  lastId: TObjectId;
}

function FetchController<TObject = unknown, TObjectId extends string = string>({
  children,
  fetchObject,
  injectAs = "object",
  object,
  objectId,
  objectLoaded,
  unloadObject,
}: FetchControllerProps<TObject, TObjectId>) {
  const isMountedRef = useRef(false);
  const cancelSourceRef = useRef<{ cancel: () => void } | null>(null);

  const [{ inFlight, lastId }, setState] = useState<FetchState<TObjectId>>({
    inFlight: false,
    lastId: objectId,
  });

  const shouldFetch = useMemo(() => {
    return !inFlight && objectId !== lastId;
  }, [inFlight, lastId, objectId]);

  const doFetch = useCallback(
    (opts: Record<string, unknown> = {}) => {
      const params = {};

      const options = {
        ...opts,
        ...genCancelSourceOptions(),
      } as { cancelSource?: { cancel: () => void } };

      cancelSourceRef.current = options.cancelSource ?? null;

      return fetchObject(objectId, params, options)
        .then(
          (result) => result,
          (err) => {
            throw err;
          },
        )
        .then((result) => {
          if (isMountedRef.current) {
            setState((prev) => ({ ...prev, inFlight: false }));
          }

          return result;
        })
        .catch((err) => {
          if (isMountedRef.current) {
            setState((prev) => ({ ...prev, inFlight: false }));
          }

          throw err;
        });
    },
    [fetchObject, objectId],
  );

  const beginFetch = useCallback(
    (opts?: Record<string, unknown>) => {
      if (unloadObject) {
        unloadObject();
      }

      setState({ inFlight: true, lastId: objectId });
      return doFetch(opts);
    },
    [doFetch, objectId, unloadObject],
  );

  useEffect(() => {
    isMountedRef.current = true;
    beginFetch();

    return () => {
      isMountedRef.current = false;
      cancelSourceRef.current?.cancel();
      unloadObject?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldFetch) {
      beginFetch();
    }
  }, [beginFetch, shouldFetch]);

  if (inFlight) {
    return (
      <Container key="fetch" p={1}>
        <NavLoader.Proxy>Fetching...</NavLoader.Proxy>
      </Container>
    );
  }

  if (!objectLoaded) {
    return (
      <Container key="stores" p={1}>
        <NavLoader.Proxy />
      </Container>
    );
  }

  if (!object) {
    return <Redirect to="/404" />;
  }

  if (children) {
    return cloneElement(children, { [injectAs]: object } as Record<
      string,
      unknown
    >);
  }

  return null;
}

export { FetchController };
